import React, { useMemo, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { useAppearance } from "../../context/AppearanceContext";
import { seasonKey } from "../../lib/themes";

// A gentle, dismissible seasonal nudge. Not a pop-up: it sits quietly in the corner, offers a
// one-tap apply, and respects the user's "seasonal suggestions" preference. Dismissal is remembered
// per season so the same nudge is never shown twice in one season.
export default function SeasonalSuggestion() {
  const { prefs, update, seasonalTheme, resolvedTheme, applyTheme } = useAppearance();
  const key = useMemo(() => seasonKey(seasonalTheme), [seasonalTheme]);
  const [gone, setGone] = useState(false);

  const dismissed = (prefs.dismissedSeasons || []).includes(key);
  const show = prefs.seasonalSuggestions && !prefs.autoSwitch && seasonalTheme
    && resolvedTheme !== seasonalTheme.id && !dismissed && !gone;

  if (!show) return null;

  const dismiss = () => { setGone(true); update({ dismissedSeasons: [ ...(prefs.dismissedSeasons || []), key ] }); };
  const apply = () => { applyTheme(seasonalTheme.id); setGone(true); };

  return (
    <div data-testid="seasonal-suggestion"
      className="fixed bottom-5 left-5 z-[70] w-[19rem] max-w-[calc(100vw-2.5rem)] animate-[ssIn_.3s_ease-out] overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_18px_44px_rgba(45,52,54,0.18)]">
      <div className="h-16 w-full theme-band" style={{ backgroundImage: `linear-gradient(135deg, ${seasonalTheme.card.from}, ${seasonalTheme.card.to})` }} />
      <img src={seasonalTheme.poses?.cheer || seasonalTheme.planni} alt="Planni" className="absolute right-3 top-1 h-20 w-20 object-contain" />
      <button data-testid="suggestion-dismiss" onClick={dismiss} aria-label="Dismiss" className="absolute right-2 top-2 rounded-full bg-white/70 p-1 text-[#636E72] backdrop-blur hover:text-[#2D3436]"><X className="h-3.5 w-3.5" /></button>
      <div className="p-4 pt-2">
        <p className="text-sm font-semibold text-[#2D3436]">{seasonalTheme.emoji} {seasonalTheme.name} is here</p>
        <p className="mt-0.5 text-xs leading-relaxed text-[#636E72]">Would you like to decorate your PlannioLife for the season? You can change it back any time.</p>
        <div className="mt-3 flex items-center gap-2">
          <button data-testid="suggestion-apply" onClick={apply}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[var(--theme-chip)] px-3 py-2 text-xs font-semibold text-[var(--theme-on-accent)] transition-transform hover:-translate-y-0.5">
            <Sparkles className="h-3.5 w-3.5" /> Try it
          </button>
          <button data-testid="suggestion-later" onClick={dismiss} className="rounded-xl px-3 py-2 text-xs font-medium text-[#636E72] hover:bg-[#f0f1ee]">Not now</button>
        </div>
      </div>
      <style>{`@keyframes ssIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
